import {KilnInfo} from '@domain/kiln-monitoring'
import {materialsRecipesQueryOptions, MaterialsRecipesQueryKey} from '@domain/materials'
import {getPermissionsByUserId} from '@hconnect/apiclient'
import {
  Asset,
  AssetsTransitions,
  AssetLink,
  UpmEntityType,
  PlantNode,
  AssetLinkType,
  EntityConfigDto,
  KpiResponse
} from '@hconnect/common/types'

import type {
  AssetsStandardOperationTimes,
  DefaultAssetsOperationTimes
} from '../modules/assets/types'
import {validateBaseLoadInAssets} from '../modules/assets/utils'
import {validateAssetOperationModesHaveExistingRecipes} from '../modules/assets/utils/validators/assets'
import {validateScheduleItemsHaveExistingOperationModes} from '../modules/common/helpers/schedule'
import type {Schedule, PlantsResponse, PlantDetails} from '../modules/common/types'
import {Constraints} from '../modules/common/types/contstraints'
import {Latest} from '../modules/common/types/latest'
import type {PermissionParsed} from '../modules/permissions/types'
import {
  FunctionsResponse,
  JanusSpecificKpi,
  KpiNamesResponse,
  LastResultsResponse,
  SensorsResponse,
  type KpiPayload,
  UnitsResponse
} from '../modules/plant-setup/plant-setup-tabs/kpis/types'

import {Api} from './api'

import {
  fetchSettingsQuery,
  removeSettingsQuery
} from '@settings/modules/common/providers/ApiInterface'
import {settingsQueryClient} from '@settings/modules/common/providers/QueryProvider'
import {measurementsQueries} from '@settings/modules/measurements/api/queries'
import {GrindingCircuitResponse} from '@settings/modules/plant-setup/types'
import {AreaDetails, ProductionLine} from '@settings/modules/plant-setup/types/departmentConfig'
import {samplingPointsQueries} from '@settings/modules/sampling-points/api/queries'
import {DepartmentType, UpmTypeBase} from '@settings/modules/upm/types/common'
import {Equipment} from '@settings/modules/upm/types/equipment'

const plannerDSApiBasePath = '/hcem/ui/v2'

export const queries = {
  defaultAssetsOperationTimes: async ({
    assetsIds,
    plantCode
  }: {
    assetsIds: number[]
    plantCode: string
  }): Promise<DefaultAssetsOperationTimes> => {
    const path = `/schedules/default-asset-operation-times/${plantCode}`
    const params: Record<string, unknown> = {
      assetIds: assetsIds.join(',')
    }
    const response = await Api.axiosInstance.get<DefaultAssetsOperationTimes>(path, {params})
    return response.data
  },
  plants: async (): Promise<PlantsResponse> => {
    const path = '/plants'
    const response = await Api.axiosInstance.get<PlantsResponse>(path)
    return response.data
  },
  permissions: async (userId: string): Promise<PermissionParsed[]> => {
    const permissions = await getPermissionsByUserId(Api.axiosInstance)(userId)
    const permissionsParsed: PermissionParsed[] = permissions.map((permission) => ({
      ...permission,
      parsedDataScope: JSON.parse(permission.dataScope) as PermissionParsed['parsedDataScope']
    }))
    return permissionsParsed
  },
  schedule: async ({
    plantCode,
    start,
    end
  }: {
    plantCode: string
    start: string
    end: string
  }): Promise<Schedule> => {
    const path = '/schedules/latest'
    const params: Record<string, string> = {
      start,
      end,
      plantCode: plantCode
    }
    const response = await Api.axiosInstance.get<Schedule>(path, {params})
    const assets = await fetchSettingsQuery('assets', plantCode)
    try {
      validateScheduleItemsHaveExistingOperationModes(response.data.schedules, assets)
    } catch (error) {
      removeSettingsQuery('assets', plantCode)
      throw error
    }
    return response.data
  },
  assetsStandardOperationTimes: async ({
    assetsIds,
    plantCode
  }: {
    assetsIds: number[]
    plantCode: string
  }): Promise<AssetsStandardOperationTimes> => {
    const path = `/plants/${plantCode}/schedules/standard-operation-times`
    const params: Record<string, unknown> = {
      assetIds: assetsIds.join(',')
    }
    const response = await Api.axiosInstance.get<AssetsStandardOperationTimes>(path, {params})
    return response.data
  },
  latest: async (plantCode?: string): Promise<Latest | undefined> => {
    if (plantCode === undefined) return undefined
    const path = `${plannerDSApiBasePath}/latest/${plantCode}`
    const response = await Api.axiosInstance.get<Latest>(path)
    return response.data
  },
  constraints: async (plantCode: string): Promise<Constraints> => {
    const path = `${plannerDSApiBasePath}/optimizer/${plantCode}/constraints`
    const response = await Api.axiosInstance.get<Constraints>(path)
    return response.data
  },
  assets: async (plantCode: string): Promise<Asset[]> => {
    const path = `/plants/${plantCode}/assets`
    const {data} = await Api.axiosInstance.get<Asset[]>(path)
    // checking if base load is correctly added from BE
    validateBaseLoadInAssets(data)
    const recipes = await settingsQueryClient.fetchQuery(materialsRecipesQueryOptions(plantCode))

    try {
      validateAssetOperationModesHaveExistingRecipes(data, recipes)
    } catch (error) {
      settingsQueryClient.removeQueries({queryKey: [MaterialsRecipesQueryKey, plantCode]})
      throw error
    }
    return data
  },
  assetsTransitions: async (plantCode: string): Promise<AssetsTransitions> => {
    const path = `/plants/${plantCode}/assets/transitions`
    const response = await Api.axiosInstance.get<AssetsTransitions>(path)
    return response.data
  },
  assetsLinks: async ({
    plantCode,
    assetId,
    fromTypes,
    toTypes
  }: {
    plantCode: string
    assetId: number
    fromTypes?: AssetLinkType[]
    toTypes?: AssetLinkType[]
  }): Promise<AssetLink[]> => {
    const path = `/plants/${plantCode}/assets/${assetId}/links`
    const params: Record<string, unknown> = {
      ...(fromTypes && {fromTypes: fromTypes.join(',')}),
      ...(toTypes && {toTypes: toTypes.join(',')})
    }
    const response = await Api.axiosInstance.get<AssetLink[]>(path, {params})
    return response.data
  },
  kpis: async ({plantCode, search}: {plantCode: string; search: string}): Promise<KpiResponse> => {
    const path = `/kpieditor/plants/${plantCode}/kpis`
    const params = search.length ? {search} : undefined
    const response = await Api.axiosInstance.get<KpiResponse>(path, {params})
    return response.data
  },
  upmKpis: async ({
    plantCode,
    upmId,
    includeChildren,
    search
  }: {
    plantCode: string
    upmId: string
    includeChildren: boolean
    search: string
  }): Promise<KpiResponse> => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${upmId}`
    const params = {includeChildren, ...(search.length ? {search} : {})}
    const response = await Api.axiosInstance.get<KpiResponse>(path, {params})
    return response.data
  },
  janusSpecificKpis: async ({
    upmPlantId,
    upmId,
    upmMappingType,
    unitId
  }: {
    upmPlantId: string
    upmId: string
    upmMappingType: number
    unitId: string | null
  }): Promise<JanusSpecificKpi[]> => {
    const path = `janus-kpi/janus-specific-kpi/${upmPlantId}?upmId=${upmId}&upmMappingType=${upmMappingType}&unitTypeId=${unitId}`
    const response = await Api.axiosInstance.get<JanusSpecificKpi[]>(path)
    return response.data
  },
  janusSpecificKpisByDefinition: async ({
    upmId,
    definitionId
  }: {
    upmId?: string
    definitionId?: number
  }): Promise<JanusSpecificKpi[]> => {
    const path = `/janus-kpi/janus-specific-kpi/${upmId}/by-definition`
    const response = await Api.axiosInstance.get<JanusSpecificKpi[]>(path, {params: {definitionId}})
    return response.data
  },
  units: async (): Promise<string[]> => {
    const path = '/kpieditor/plants/kpiunits'
    const response = await Api.axiosInstance.get<UnitsResponse>(path)
    return response.data || []
  },
  kpiUnits: async (plantCode: string, kpiId: number): Promise<string[]> => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/units`
    const response = await Api.axiosInstance.get<UnitsResponse>(path)
    return response.data || []
  },
  sensors: async (plantCode: string): Promise<SensorsResponse> => {
    const path = `/kpieditor/plants/${plantCode}/sensors`
    const response = await Api.axiosInstance.get<SensorsResponse>(path)
    return response.data
  },
  functions: async (plantCode: string): Promise<FunctionsResponse> => {
    const path = `/kpieditor/plants/${plantCode}/functions`
    const response = await Api.axiosInstance.get<FunctionsResponse>(path)
    return response.data
  },
  kpiNames: async (): Promise<KpiNamesResponse> => {
    const path = '/kpieditor/plants/kpinames'
    const response = await Api.axiosInstance.get<KpiNamesResponse>(path)
    return response.data || []
  },
  lastResults: async (plantCode: string, kpiId: number): Promise<LastResultsResponse> => {
    const path = `/kpieditor/plants/${plantCode}/kpis/${kpiId}/last-results`
    const response = await Api.axiosInstance.get<LastResultsResponse>(path)
    return response.data
  },
  sentDateToVerifyFormula: async ({
    plantCode,
    formula,
    from,
    to
  }: {
    plantCode: string
    formula: string | null
    from: Date
    to: Date
  }) => {
    const path = `/kpi-result/${plantCode}/validate-kpi`
    const params = {
      from,
      to,
      kpiFormula: formula
    }
    const response = await Api.axiosInstance.get<KpiPayload>(path, {params})
    return response.data
  },
  assetsGeneralInfo: async (plantCode: string): Promise<Asset[]> => {
    const path = `/plants/${plantCode}/assets`
    const {data} = await Api.axiosInstance.get<Asset[]>(path)

    return data
  },
  plantDetails: async (plantId?: string): Promise<PlantDetails> => {
    const path = `/upm/plant/${plantId}`
    const response = await Api.axiosInstance.get<PlantDetails>(path)
    return response.data
  },
  plantConfig: async (upmPlantId?: string): Promise<EntityConfigDto[]> => {
    const path = `asset-config/janus-production-data/plant/${upmPlantId}`
    const response = await Api.axiosInstance.get<EntityConfigDto[]>(path)
    return response.data
  },
  upmId: async (plantId?: string): Promise<{upmId: string}> => {
    const path = `/kms/plants/${plantId}`
    const response = await Api.axiosInstance.get<{upmId: string}>(path)
    return response.data
  },
  upmEntityConfig: async (
    upmPlantId?: string,
    departmentId?: string,
    type?: UpmEntityType
  ): Promise<EntityConfigDto> => {
    const path = `asset-config/janus-production-data/plant/${upmPlantId}/upm-type/${type}?typeUpmId=${departmentId}`
    const response = await Api.axiosInstance.get<EntityConfigDto>(path)
    return response.data
  },
  upmDepartmentTypes: async (): Promise<DepartmentType[]> => {
    const path = 'upm/department-type/'
    const response = await Api.axiosInstance.get<DepartmentType[]>(path)
    return response.data
  },
  upmAreaTypes: async (): Promise<UpmTypeBase[]> => {
    const path = 'upm/area-type/'
    const response = await Api.axiosInstance.get<UpmTypeBase[]>(path)
    return response.data
  },
  upmAllAreaTypes: async (): Promise<UpmTypeBase[]> => {
    const path = 'upm/area-type'
    const response = await Api.axiosInstance.get<UpmTypeBase[]>(path)
    return response.data
  },
  upmDepartmentDetails: async (upmId?: string): Promise<Equipment> => {
    const path = `upm/department/${upmId}`
    const response = await Api.axiosInstance.get<Equipment>(path)
    return response.data
  },
  upmAreaDetails: async (upmId?: string): Promise<AreaDetails> => {
    const path = `upm/area/${upmId}`
    const response = await Api.axiosInstance.get<AreaDetails>(path)
    return response.data
  },
  upmProductionLines: async (): Promise<ProductionLine[]> => {
    const path = 'upm/production-line'
    const response = await Api.axiosInstance.get<ProductionLine[]>(path)
    return response.data
  },
  upmOperationalStatuses: async (): Promise<UpmTypeBase[]> => {
    const path = '/upm/operational-status'
    const response = await Api.axiosInstance.get<UpmTypeBase[]>(path)
    return response.data
  },
  grindingCircuits: async (plantCode: string): Promise<GrindingCircuitResponse> => {
    const path = `/plants/${plantCode}/grinding-circuits`
    const response = await Api.axiosInstance.get<GrindingCircuitResponse>(path)
    return response.data
  },
  upmStructure: async (
    plantCode: string,
    includeEquipment: boolean,
    product: string | undefined
  ): Promise<PlantNode> => {
    const path = `upm/plants/${plantCode}/structure`
    return (
      await Api.axiosInstance.get<PlantNode>(path, {
        params: {includeEquipment, product}
      })
    ).data
  },
  upmRemovedStructure: async (
    plantCode: string,
    includeEquipment: boolean,
    product: string | undefined
  ): Promise<PlantNode> => {
    const path = `upm/plants/${plantCode}/structure/removed`
    return (
      await Api.axiosInstance.get<PlantNode>(path, {
        params: {includeEquipment, product}
      })
    ).data
  },
  kilnInfo: async (plantCode: string): Promise<KilnInfo[]> => {
    const path = `/kms/${plantCode}/kilns`
    return (await Api.axiosInstance.get<KilnInfo[]>(path)).data
  },
  ...measurementsQueries,
  ...samplingPointsQueries
}

export type Queries = typeof queries
